/**
 * Portal v2 sender detail (`/portal/senders/$sourceIp`).
 *
 * The per-domain breakdown is the reason this page exists: one source passing
 * for one domain and failing for another is what distinguishes a misconfigured
 * legitimate sender from someone spoofing the customer. A single blended
 * pass rate hides exactly that.
 *
 * DKIM and SPF are shown separately because the fix differs — a DKIM-only
 * failure is usually a signing/selector problem, an SPF-only failure usually a
 * missing include or a forwarder.
 */
import { Link, useParams } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { format } from "date-fns";
import { ArrowLeft, Server } from "lucide-react";
import { Button, Skeleton } from "@ddmarc/ui";
import { portalApi } from "@/lib/portal-api";
import { CardV2, KpiTile, SectionHeader, StatPill } from "@/components/primitives";

const passRateTone = (pct: number): "good" | "warn" | "danger" =>
  pct >= 95 ? "good" : pct >= 80 ? "warn" : "danger";

export function PortalSenderDetailV2Page() {
  const { sourceIp } = useParams({ from: "/portal/senders/$sourceIp" });

  const { data, isLoading, isError } = useQuery({
    queryKey: ["portal", "sender", sourceIp],
    queryFn: () => portalApi.getSender(sourceIp),
  });

  const backLink = (
    <Link to="/portal/senders">
      <Button variant="ghost" size="sm" className="gap-1.5 px-2 text-[12.5px]">
        <ArrowLeft className="h-3.5 w-3.5" />
        Senders
      </Button>
    </Link>
  );

  if (isLoading) {
    return (
      <div className="space-y-3.5">
        {backLink}
        <Skeleton className="h-24 w-full" />
        <Skeleton className="h-48 w-full" />
      </div>
    );
  }

  // A sender that never sent for this customer 404s server-side — including one
  // that only ever sent for a different organisation.
  if (isError || !data) {
    return (
      <div className="space-y-3.5">
        {backLink}
        <CardV2 title="Sender not found">
          <p className="text-[12.5px] text-muted-foreground">
            No mail from this source has been reported for your domains.
          </p>
        </CardV2>
      </div>
    );
  }

  const dkimRate = data.total_emails
    ? (data.dkim_pass_emails / data.total_emails) * 100
    : 0;
  const spfRate = data.total_emails
    ? (data.spf_pass_emails / data.total_emails) * 100
    : 0;

  return (
    <div className="space-y-3.5">
      {backLink}

      <SectionHeader
        title={
          <span className="flex items-center gap-2">
            <Server className="h-5 w-5" />
            <code className="font-mono-data">{data.source_ip}</code>
          </span>
        }
        subtitle={[data.asn_org, data.country, data.ptr].filter(Boolean).join(" · ") || "Sending source"}
      />

      <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-4">
        <KpiTile
          label="Messages"
          value={data.total_emails.toLocaleString()}
          hint="Across your domains"
        />
        <KpiTile
          label="Pass rate"
          value={`${data.pass_rate.toFixed(1)}%`}
          hint={`${data.failed_emails.toLocaleString()} failed`}
        />
        <KpiTile label="DKIM" value={`${dkimRate.toFixed(1)}%`} hint="Signed and aligned" />
        <KpiTile label="SPF" value={`${spfRate.toFixed(1)}%`} hint="Authorized and aligned" />
      </div>

      <CardV2
        title="Per-domain results"
        subtitle="The same source can be configured correctly for one domain and not another."
        bodyClassName="p-0"
      >
        <div className="divide-y divide-border">
          <div className="grid grid-cols-[1.6fr_0.7fr_0.7fr_0.7fr] gap-3 px-4 py-2.5 text-[10.5px] font-semibold uppercase tracking-[0.05em] text-[var(--v2-fg-subtle)]">
            <span>Domain</span>
            <span className="text-right">Messages</span>
            <span className="text-right">Failed</span>
            <span className="text-right">Pass</span>
          </div>
          {data.domains.map((row) => (
            <div
              key={row.domain_id}
              className="grid grid-cols-[1.6fr_0.7fr_0.7fr_0.7fr] items-center gap-3 px-4 py-[var(--v2-row-py,0.625rem)] text-[12.5px]"
            >
              <Link
                to="/portal/domains/$domainId"
                params={{ domainId: String(row.domain_id) }}
                className="truncate font-mono-data text-foreground underline-offset-2 hover:underline"
              >
                {row.domain}
              </Link>
              <span className="tnum text-right text-foreground">
                {row.total_emails.toLocaleString()}
              </span>
              <span className="tnum text-right text-[var(--v2-danger)]">
                {row.failed_emails.toLocaleString()}
              </span>
              <span className="text-right">
                <StatPill tone={passRateTone(row.pass_rate)}>
                  {row.pass_rate.toFixed(1)}%
                </StatPill>
              </span>
            </div>
          ))}
        </div>
      </CardV2>

      {(data.first_seen || data.last_seen) && (
        <p className="text-[12px] text-muted-foreground">
          Seen {data.first_seen ? format(new Date(data.first_seen), "MMM d, yyyy") : "—"} to{" "}
          {data.last_seen ? format(new Date(data.last_seen), "MMM d, yyyy") : "—"}.
        </p>
      )}
    </div>
  );
}
